// src/index.ts
import {
  isValidationErrorResponse,
  getOriginalObject
} from "@rvf/core";

// src/base.tsx
import { useEffect as useEffect2, useMemo as useMemo4 } from "react";
import {
  scopeFormScope as scopeFormScope3,
  getFieldValue as getFieldValue4,
  getFieldDefaultValue as getFieldDefaultValue2,
  getFieldTouched as getFieldTouched2,
  getFieldDirty as getFieldDirty2,
  getFieldError as getFieldError3,
  focusFirst,
  getAllTouched,
  getAllDirty,
  getAllErrors,
  getFormAction,
  getFormId as getFormId2,
  getFormProps,
  FORM_ID_FIELD_NAME,
  getFormIdOption
} from "@rvf/core";
import {
  getPath,
  mergePathStrings as mergePathStrings2
} from "@rvf/set-get";

// src/array.tsx
import { useMemo as useMemo2 } from "react";
import {
  getFieldError,
  getFieldValue as getFieldValue2,
  scopeFormScope as scopeFormScope2,
  getArrayUpdateKey
} from "@rvf/core";

// src/implFactory.tsx
import { mergePathStrings } from "@rvf/set-get";
var makeImplFactory = (prefix, create) => {
  const implCache = /* @__PURE__ */ new Map();
  return (fieldName) => {
    const fullName = mergePathStrings(prefix, fieldName);
    const existingImpl = implCache.get(fullName);
    if (existingImpl)
      return existingImpl;
    const impl = create(fieldName ?? "");
    implCache.set(fullName, impl);
    return impl;
  };
};

// src/context.tsx
import { scopeFormScope } from "@rvf/core";
import {
  createContext,
  useMemo,
  useContext,
  useEffect
} from "react";

// src/useFormScope.tsx
function useFormScope(form) {
  return useFormInternal(form);
}

// src/context.tsx
import { jsx } from "react/jsx-runtime";
var FormContext = createContext(null);
var FormProvider = ({
  scope,
  children
}) => {
  const value = useMemo(() => ({ scope }), [scope]);
  return /* @__PURE__ */ jsx(FormContext.Provider, { value, children });
};
var useFormContext = () => {
  const value = useContext(FormContext);
  if (!value)
    throw new Error("useFormContext must be used within a FormProvider");
  return useFormScope(value.scope);
};
var useFormScopeOrContextInternal = (rvfOrName) => {
  const value = useContext(FormContext);
  const getFormScope = () => {
    if (rvfOrName == null) {
      if (!value)
        throw new Error("useFormContext must be used within a FormProvider");
      return value.scope;
    }
    if (typeof rvfOrName !== "string")
      return rvfOrName;
    if (!value)
      throw new Error("useFormContext must be used within a FormProvider");
    return scopeFormScope(value.scope, rvfOrName);
  };
  const rvf = getFormScope();
  useEffect(() => {
    rvf.__store__.resolvers.flush();
  });
  return rvf;
};
var useFormScopeOrContext = (rvf) => {
  const value = useContext(FormContext);
  const scope = value?.scope ?? rvf;
  if (!scope)
    throw new Error(
      "useFormScopeOrContext must be passed an FormScope or be used within a FormProvider"
    );
  return useFormScope(scope);
};

// src/refs.ts
import {
  getFieldValue,
  isFormControl,
  setFormControlValue
} from "@rvf/core";
var createTransientRef = (fieldName, form) => {
  const sym = Symbol(fieldName);
  return (el) => {
    if (el == null) {
      form.__store__.transientFieldRefs.removeRef(fieldName, sym);
      return;
    }
    form.__store__.transientFieldRefs.setRef(fieldName, el, sym);
    if (isFormControl(el)) {
      const value = getFieldValue(form.__store__.store.getState(), fieldName);
      if (value != null)
        setFormControlValue(el, value);
    }
  };
};
var createControlledRef = (fieldName, form) => {
  const sym = Symbol(fieldName);
  return (el) => {
    if (el == null) {
      form.__store__.controlledFieldRefs.removeRef(fieldName, sym);
      return;
    }
    form.__store__.controlledFieldRefs.setRef(fieldName, el, sym);
  };
};

// src/array.tsx
var makeFieldArrayImpl = ({
  form,
  trackedState,
  validationBehavior
}) => {
  const arrayFieldName = form.__field_prefix__;
  const itemImpl = makeImplFactory(
    arrayFieldName,
    (itemFieldName) => makeBaseFormApi({
      form: scopeFormScope2(form, itemFieldName),
      trackedState
    })
  );
  const length = () => {
    const val = getFieldValue2(trackedState, arrayFieldName);
    if (val == null)
      return 0;
    if (Array.isArray(val))
      return val.length;
    console.warn(
      "Tried to treat a non-array as an array. Make sure you used the correct field name and set a default value."
    );
    return 0;
  };
  return {
    errorFocusElement: createControlledRef(arrayFieldName, form),
    name: () => arrayFieldName,
    error: () => getFieldError(trackedState, arrayFieldName),
    length,
    map: (callback) => {
      getArrayUpdateKey(trackedState, arrayFieldName);
      return trackedState.getFieldArrayKeys(arrayFieldName).map((key, index) => {
        const itemFormScope = itemImpl(String(index));
        return callback(key, itemFormScope, index);
      });
    },
    keys: () => {
      getArrayUpdateKey(trackedState, arrayFieldName);
      return trackedState.getFieldArrayKeys(arrayFieldName);
    },
    push: (value) => {
      trackedState.arrayPush(arrayFieldName, value, validationBehavior);
      return form.__store__.resolvers.await();
    },
    pop: () => {
      trackedState.arrayPop(arrayFieldName, validationBehavior);
      return form.__store__.resolvers.await();
    },
    shift: () => {
      trackedState.arrayShift(arrayFieldName, validationBehavior);
      return form.__store__.resolvers.await();
    },
    unshift: (value) => {
      trackedState.arrayUnshift(arrayFieldName, value, validationBehavior);
      return form.__store__.resolvers.await();
    },
    insert: (index, value) => {
      trackedState.arrayInsert(
        arrayFieldName,
        index,
        value,
        validationBehavior
      );
      return form.__store__.resolvers.await();
    },
    move: (fromIndex, toIndex) => {
      trackedState.arrayMove(
        arrayFieldName,
        fromIndex,
        toIndex,
        validationBehavior
      );
      return form.__store__.resolvers.await();
    },
    remove: (index) => {
      trackedState.arrayRemove(arrayFieldName, index, validationBehavior);
      return form.__store__.resolvers.await();
    },
    swap: (fromIndex, toIndex) => {
      trackedState.arraySwap(
        arrayFieldName,
        fromIndex,
        toIndex,
        validationBehavior
      );
      return form.__store__.resolvers.await();
    },
    replace: (index, value) => {
      trackedState.arrayReplace(
        arrayFieldName,
        index,
        value,
        validationBehavior
      );
      return form.__store__.resolvers.await();
    }
  };
};
function useFieldArray(formOrName, { validationBehavior } = {}) {
  const scope = useFormScopeOrContextInternal(formOrName);
  const { useStoreState } = scope.__store__;
  const trackedState = useStoreState();
  trackedState.setValue;
  const base = useMemo2(
    () => makeFieldArrayImpl({
      form: scope,
      trackedState,
      validationBehavior
    }),
    [scope, trackedState, validationBehavior]
  );
  return base;
}
function FieldArray(props) {
  const field = (
    // not actually breaking rules here
    // eslint-disable-next-line react-hooks/rules-of-hooks
    "name" in props ? useFieldArray(props.name) : useFieldArray(props.scope)
  );
  return props.children(field);
}

// src/field.tsx
import { useMemo as useMemo3 } from "react";
import {
  getFieldDefaultValue,
  getFieldDirty,
  getFieldError as getFieldError2,
  getFieldTouched,
  getFieldValue as getFieldValue3,
  getFormId
} from "@rvf/core";

// src/inputs/getInputProps.ts
import {
  getCheckboxChecked,
  getNextNativeValue,
  getRadioChecked,
  isEvent,
  isFormControl as isFormControl2
} from "@rvf/core";
var omitBy = (obj, predicate) => Object.fromEntries(
  Object.entries(obj).filter(([key, value]) => !predicate(value, key))
);
var createGetInputProps = ({
  onChange,
  onBlur,
  defaultValue,
  name,
  createRef,
  formId,
  getCurrentValue
}) => {
  return (props = {}) => {
    const rvfRef = createRef();
    const inputProps = {
      ...props,
      form: formId,
      onChange: (...args) => {
        props?.onChange?.(...args);
        if (!isEvent(args[0])) {
          onChange(args[0]);
          return;
        }
        const target = args[0].target;
        if (!isFormControl2(target)) {
          onChange(args[0]);
          return;
        }
        const nextValue = getNextNativeValue({
          element: target,
          currentValue: getCurrentValue()
        });
        onChange(nextValue);
      },
      onBlur: (...args) => {
        props?.onBlur?.(...args);
        onBlur();
      },
      name,
      ref: (element) => {
        if (typeof props.ref === "function")
          props.ref(element);
        else if (props.ref)
          props.ref.current = element;
        if (typeof rvfRef === "function")
          rvfRef(element);
        else if (rvfRef)
          rvfRef.current = element;
      }
    };
    if (props.type === "checkbox") {
      inputProps.defaultChecked = getCheckboxChecked(props.value, defaultValue);
    } else if (props.type === "radio") {
      inputProps.defaultChecked = getRadioChecked(props.value, defaultValue);
    } else if (props.value === void 0 && inputProps.type !== "file") {
      inputProps.defaultValue = defaultValue;
    }
    if (props.type === "file" && !!defaultValue) {
      console.warn("File inputs cannot have a default value.");
    }
    return omitBy(inputProps, (value) => value === void 0);
  };
};

// src/field.tsx
var makeFieldImpl = ({
  form,
  trackedState,
  validationBehavior
}) => {
  const fieldName = form.__field_prefix__;
  const onChange = (value) => trackedState.onFieldChange(fieldName, value, validationBehavior);
  const onBlur = () => trackedState.onFieldBlur(fieldName, validationBehavior);
  const createSerializerRef = (serialize) => {
    const sym = Symbol(fieldName);
    return (el) => {
      if (el == null) {
        form.__store__.fieldSerializerRefs.removeRef(fieldName, sym);
        return;
      }
      form.__store__.fieldSerializerRefs.setRef(fieldName, serialize, sym);
    };
  };
  return {
    getInputProps: createGetInputProps({
      onChange,
      onBlur,
      defaultValue: getFieldDefaultValue(trackedState, fieldName),
      name: fieldName,
      createRef: () => createTransientRef(fieldName, form),
      formId: getFormId(trackedState),
      getCurrentValue: () => getFieldValue3(form.__store__.store.getState(), fieldName)
    }),
    getControlProps: (props = {}) => ({
      name: fieldName,
      onChange: (value) => {
        props.onChange?.(value);
        onChange(value);
      },
      onBlur: () => {
        props.onBlur?.();
        onBlur();
      },
      value: getFieldValue3(trackedState, fieldName),
      ref: createControlledRef(fieldName, form)
    }),
    getHiddenInputProps: ({
      serialize = (val) => val
    } = {}) => ({
      name: fieldName,
      value: serialize(getFieldValue3(trackedState, fieldName)),
      type: "hidden",
      form: getFormId(trackedState),
      ref: createSerializerRef(serialize)
    }),
    refs: {
      transient: () => createTransientRef(fieldName, form),
      controlled: () => createControlledRef(fieldName, form)
    },
    name: () => fieldName,
    onChange,
    onBlur,
    value: () => getFieldValue3(trackedState, fieldName),
    setValue: (value) => trackedState.setValue(fieldName, value),
    defaultValue: () => getFieldDefaultValue(trackedState, fieldName),
    touched: () => getFieldTouched(trackedState, fieldName),
    setTouched: (value) => trackedState.setTouched(fieldName, value),
    dirty: () => getFieldDirty(trackedState, fieldName),
    setDirty: (value) => trackedState.setDirty(fieldName, value),
    error: () => getFieldError2(trackedState, fieldName),
    clearError: () => trackedState.setError(fieldName, null),
    reset: () => trackedState.resetField(fieldName),
    validate: () => {
      void trackedState.validate();
    }
  };
};
function useField(formOrName, opts) {
  const scope = useFormScopeOrContextInternal(formOrName);
  const prefix = scope.__field_prefix__;
  const trackedState = scope.__store__.useStoreState();
  trackedState.setValue;
  const base = useMemo3(
    () => makeFieldImpl({
      form: scope,
      trackedState,
      validationBehavior: opts?.validationBehavior
    }),
    [opts?.validationBehavior, scope, trackedState]
  );
  return base;
}
function Field(props) {
  const field = "name" in props ? useField(props.name) : useField(props.scope);
  return props.children(field);
}

// src/base.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var makeBaseFormApi = ({
  trackedState,
  form
}) => {
  const prefix = form.__field_prefix__;
  const f = (fieldName) => mergePathStrings2(prefix, fieldName);
  const transientState = () => form.__store__.store.getState();
  const optionalField = (args) => args.length === 1 ? [prefix, args[0]] : [f(args[0]), args[1]];
  const optionalNameAndOptions = (args) => {
    const [a, b] = args;
    if (typeof a === "string")
      return [f(a), b];
    return [prefix, a];
  };
  const arrayImpl = makeImplFactory(
    prefix,
    (arrayFieldName) => makeFieldArrayImpl({
      trackedState,
      form: scopeFormScope3(form, arrayFieldName)
    })
  );
  const fieldImpl = makeImplFactory(
    prefix,
    (fieldName) => makeFieldImpl({
      form: scopeFormScope3(form, fieldName),
      trackedState
    })
  );
  const makeFormState = (state) => ({
    get isSubmitting() {
      return state().submitStatus === "submitting";
    },
    get hasBeenSubmitted() {
      return state().submitStatus !== "idle";
    },
    get isDirty() {
      return Object.values(state().dirtyFields).some(Boolean);
    },
    get isTouched() {
      return Object.values(state().touchedFields).some(Boolean);
    },
    get isValid() {
      return Object.values(state().validationErrors).every((error) => !error);
    },
    get submitStatus() {
      return state().submitStatus;
    },
    get touchedFields() {
      return getAllTouched(state());
    },
    get dirtyFields() {
      return getAllDirty(state());
    },
    get fieldErrors() {
      return getAllErrors(state());
    }
  });
  return {
    value: (fieldName) => getFieldValue4(trackedState, f(fieldName)),
    defaultValue: (fieldName) => getFieldDefaultValue2(trackedState, f(fieldName)),
    touched: (fieldName) => getFieldTouched2(trackedState, f(fieldName)),
    dirty: (fieldName) => getFieldDirty2(trackedState, f(fieldName)),
    error: (fieldName) => getFieldError3(trackedState, f(fieldName)),
    transient: {
      value: (fieldName) => getFieldValue4(transientState(), f(fieldName)),
      defaultValue: (fieldName) => getFieldDefaultValue2(transientState(), f(fieldName)),
      touched: (fieldName) => getFieldTouched2(transientState(), f(fieldName)),
      dirty: (fieldName) => getFieldDirty2(transientState(), f(fieldName)),
      error: (fieldName) => getFieldError3(transientState(), f(fieldName)),
      formState: makeFormState(transientState)
    },
    formOptions: {
      get action() {
        return getFormAction(trackedState);
      },
      get formId() {
        return getFormId2(trackedState);
      }
    },
    formState: makeFormState(() => trackedState),
    subscribe: {
      value: (...args) => {
        const [fieldName, callback] = args.length === 1 ? [void 0, args[0]] : args;
        return form.__store__.store.subscribe((state, prevState) => {
          const value = fieldName ? getPath(state.values, fieldName) : state.values;
          const prevValue = fieldName ? getPath(prevState.values, fieldName) : prevState.values;
          if (prevValue === value)
            return;
          callback(value);
        });
      }
    },
    setValue: (...args) => transientState().setValue(...optionalField(args)),
    setDirty: (...args) => transientState().setDirty(...optionalField(args)),
    setTouched: (...args) => transientState().setTouched(...optionalField(args)),
    clearError: (fieldName) => transientState().setError(f(fieldName), null),
    focus: (fieldName) => {
      const elements = [
        ...form.__store__.transientFieldRefs.getRefs(f(fieldName)),
        ...form.__store__.controlledFieldRefs.getRefs(f(fieldName))
      ];
      focusFirst(elements);
    },
    validate: () => form.__store__.store.getState().validate().then((res) => res.errors ?? {}),
    resetForm: (...args) => {
      const formElement = form.__store__.formRef.current;
      if (formElement)
        formElement.reset();
      form.__store__.store.getState().reset(...args);
    },
    resetField: (...args) => {
      const [name, opts] = optionalNameAndOptions(
        args
      );
      return form.__store__.store.getState().resetField(name, opts);
    },
    scope: (fieldName) => fieldName == null ? form : scopeFormScope3(form, fieldName),
    name: (fieldName) => f(fieldName),
    array: arrayImpl,
    field: fieldImpl,
    getFormProps: (formProps = {}) => ({
      ...formProps,
      ...getFormProps(trackedState),
      onSubmit: (event) => {
        formProps.onSubmit?.(event);
        if (event.defaultPrevented)
          return;
        event.preventDefault();
        const nativeEvent = event.nativeEvent;
        const submitter = nativeEvent.submitter;
        const submitterData = submitter?.name ? { [submitter.name]: submitter.value } : void 0;
        const submitterOptions = {};
        if (submitter?.formEnctype)
          submitterOptions.formEnctype = submitter.formEnctype;
        if (submitter?.formMethod)
          submitterOptions.formMethod = submitter.formMethod;
        if (submitter?.formNoValidate)
          submitterOptions.formNoValidate = submitter.formNoValidate;
        const buttonFormAction = submitter?.getAttribute("formAction");
        if (buttonFormAction)
          submitterOptions.formAction = buttonFormAction;
        transientState().onSubmit(submitterData, submitterOptions);
      },
      onReset: (event) => {
        formProps.onReset?.(event);
        if (event.defaultPrevented)
          return;
        transientState().reset();
      },
      ref: (el) => {
        if (typeof formProps.ref === "function")
          formProps.ref(el);
        else if (formProps.ref) {
          formProps.ref.current = el;
        }
        form.__store__.formRef.current = el;
      }
    }),
    getInputProps: (fieldName, props) => fieldImpl(fieldName).getInputProps(props),
    getControlProps: (fieldName, props) => fieldImpl(fieldName).getControlProps(props),
    getHiddenInputProps: (fieldName, props) => fieldImpl(fieldName).getHiddenInputProps(props),
    submit: (options) => {
      const submitterData = options?.name && options?.value ? { [options.name]: options.value } : void 0;
      const submitterOptions = {
        formEnctype: options?.formEnctype,
        formMethod: options?.formMethod,
        formNoValidate: options?.formNoValidate,
        formAction: options?.formAction
      };
      trackedState.onSubmit(submitterData, submitterOptions);
    },
    renderFormIdInput: () => {
      const formId = getFormIdOption(trackedState);
      if (!formId)
        return null;
      return /* @__PURE__ */ jsx2("input", { type: "hidden", name: FORM_ID_FIELD_NAME, value: formId });
    }
  };
};
var useFormInternal = (form) => {
  const { useStoreState, resolvers } = form.__store__;
  const trackedState = useStoreState();
  useEffect2(() => {
    resolvers.flush();
  });
  trackedState.setValue;
  const base = useMemo4(
    () => makeBaseFormApi({
      form,
      trackedState
    }),
    [form, trackedState]
  );
  return base;
};

// src/useForm.tsx
import { useEffect as useEffect3, useState, useId } from "react";
import {
  createFormScope,
  registerFormElementEvents
} from "@rvf/core";
var noOp = () => {
};
var maybeThen = (maybePromise, then) => {
  if (maybePromise instanceof Promise) {
    return maybePromise.then(then);
  } else {
    return then(maybePromise);
  }
};
function useForm(options) {
  const {
    validator,
    handleSubmit: onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    onBeforeSubmit,
    onInvalidSubmit,
    submitSource,
    action,
    disableFocusOnError,
    serverValidationErrors,
    resetAfterSubmit,
    otherFormProps,
    reloadDocument,
    validationBehaviorConfig,
    id
  } = options;
  const defaultFormId = useId();
  const [form] = useState(() => {
    const rvf = createFormScope({
      defaultValues: options.defaultValues ?? {},
      serverValidationErrors: serverValidationErrors ?? {},
      validator,
      onBeforeSubmit,
      onSubmit,
      onSubmitSuccess: (data) => {
        onSubmitSuccess?.(data);
        if (resetAfterSubmit) {
          const formElement = form.__store__.formRef.current;
          if (formElement)
            formElement.reset();
          else
            form.__store__.store.getState().reset();
        }
      },
      onInvalidSubmit: onInvalidSubmit ?? noOp,
      onSubmitFailure: onSubmitFailure ?? noOp,
      validationBehaviorConfig,
      submitSource: submitSource ?? "dom",
      formProps: {
        action,
        id,
        ...otherFormProps
      },
      flags: {
        disableFocusOnError: disableFocusOnError ?? false,
        reloadDocument: reloadDocument ?? false
      },
      defaultFormId
    });
    return rvf;
  });
  useEffect3(() => {
    return registerFormElementEvents(form.__store__);
  }, [form.__store__]);
  const { initial, whenSubmitted, whenTouched } = validationBehaviorConfig ?? {};
  useEffect3(() => {
    Object.assign(form.__store__.mutableImplStore, {
      validator,
      onBeforeSubmit,
      onSubmit,
      onSubmitSuccess: (data) => {
        const successResult = onSubmitSuccess?.(data);
        return maybeThen(successResult, () => {
          if (resetAfterSubmit) {
            const formElement = form.__store__.formRef.current;
            if (formElement)
              formElement.reset();
            else
              form.__store__.store.getState().reset();
          }
        });
      },
      onInvalidSubmit,
      onSubmitFailure
    });
  }, [
    validator,
    onSubmit,
    form.__store__.mutableImplStore,
    onSubmitSuccess,
    onSubmitFailure,
    form.__store__.store,
    resetAfterSubmit,
    form.__store__.formRef,
    onInvalidSubmit,
    onBeforeSubmit
  ]);
  useEffect3(() => {
    form.__store__.store.getState().syncOptions({
      submitSource: submitSource ?? "dom",
      validationBehaviorConfig: initial && whenSubmitted && whenTouched ? {
        initial,
        whenSubmitted,
        whenTouched
      } : void 0,
      formProps: {
        action,
        id,
        ...otherFormProps
      },
      flags: {
        disableFocusOnError: disableFocusOnError ?? false,
        reloadDocument: reloadDocument ?? false
      }
    });
  }, [
    form.__store__.store,
    initial,
    submitSource,
    whenSubmitted,
    whenTouched,
    action,
    id,
    disableFocusOnError,
    otherFormProps,
    reloadDocument
  ]);
  useEffect3(() => {
    if (!serverValidationErrors)
      return;
    form.__store__.store.getState().syncServerValidationErrors(serverValidationErrors ?? {});
  }, [serverValidationErrors, form.__store__.store]);
  return useFormInternal(form);
}

// src/ValidatedForm.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var ValidatedForm = ({
  validator,
  formRef,
  defaultValues,
  serverValidationErrors,
  action,
  id,
  disableFocusOnError,
  handleSubmit,
  submitSource,
  validationBehaviorConfig,
  children,
  onBeforeSubmit,
  onSubmit,
  onReset,
  onSubmitSuccess,
  onSubmitFailure,
  onInvalidSubmit,
  resetAfterSubmit,
  otherFormProps,
  reloadDocument,
  ...rest
}) => {
  const rvf = useForm({
    defaultValues,
    serverValidationErrors,
    action,
    id,
    disableFocusOnError,
    validator,
    onBeforeSubmit,
    handleSubmit,
    submitSource,
    onSubmitSuccess,
    onSubmitFailure,
    onInvalidSubmit,
    validationBehaviorConfig,
    resetAfterSubmit,
    otherFormProps,
    reloadDocument
  });
  return /* @__PURE__ */ jsx3(FormProvider, { scope: rvf.scope(), children: /* @__PURE__ */ jsx3(
    "form",
    {
      ...rvf.getFormProps({
        onSubmit,
        onReset,
        ref: formRef
      }),
      ...rest,
      children: typeof children === "function" ? children(rvf) : children
    }
  ) });
};

// src/useNativeValidity.tsx
import {
  getElementsWithNames,
  isFormControl as isFormControl3,
  sortByPosition
} from "@rvf/core";
import { useEffect as useEffect4 } from "react";
var useNativeValidity = (ref, error) => {
  useEffect4(() => {
    if (!ref.current)
      return;
    ref.current.setCustomValidity(error ?? "");
  }, [error, ref]);
};
var useNativeValidityForForm = (scope) => {
  useEffect4(() => {
    let abortController;
    const unsubscribe = scope.__store__.store.subscribe((state, prevState) => {
      abortController?.abort();
      abortController = new AbortController();
      const signal = abortController.signal;
      const currentErrors = state.validationErrors;
      const prevErrors = prevState.validationErrors;
      const getElements = (field) => {
        const allRefs = [
          ...scope.__store__.transientFieldRefs.getRefs(field),
          ...scope.__store__.controlledFieldRefs.getRefs(field)
        ];
        if (allRefs.length > 0) {
          const registeredFormControls = sortByPosition(
            allRefs.filter(isFormControl3)
          );
          return registeredFormControls;
        }
        const formElement = scope.__store__.formRef.current;
        if (!formElement)
          return [];
        const unregisteredFormControls = sortByPosition(
          getElementsWithNames([field], formElement)
        );
        return unregisteredFormControls;
      };
      Object.keys(currentErrors).forEach((field) => {
        getElements(field).forEach((element) => {
          element.setCustomValidity(currentErrors[field]);
          if (element === document.activeElement) {
            element.reportValidity();
          }
          element.addEventListener("focus", () => element.reportValidity(), {
            signal
          });
        });
      });
      Object.keys(prevErrors).filter((key) => !currentErrors[key]).forEach((field) => {
        getElements(field).forEach(
          (element) => element.setCustomValidity("")
        );
      });
    });
    return () => {
      unsubscribe();
      abortController?.abort();
    };
  }, [scope]);
};

// src/isolation.tsx
var Isolate = ({
  scope,
  render
}) => {
  const rvf = useFormInternal(scope);
  return render(rvf);
};

// src/compatability/misc.ts
import { useCallback } from "react";
var useIsSubmitting = (rvf) => useFormScopeOrContext(rvf).formState.isSubmitting;
var useIsValid = (rvf) => useFormScopeOrContext(rvf).formState.isValid;
var useControlField = (name, rvf) => {
  const form = useFormScopeOrContext(rvf);
  const value = form.value(name);
  const setValue = useCallback(
    (value2) => form.setValue(name, value2),
    [form, name]
  );
  return [value, setValue];
};
var useUpdateControlledField = (rvf) => {
  const form = useFormScopeOrContext(rvf);
  return useCallback(
    (name, value) => form.setValue(name, value),
    [form]
  );
};
export {
  Field,
  FieldArray,
  FormProvider,
  Isolate,
  ValidatedForm,
  getOriginalObject,
  isValidationErrorResponse,
  useNativeValidityForForm as unstable_useNativeValidityForForm,
  useControlField,
  useField,
  useFieldArray,
  useForm,
  useFormContext,
  useFormScope,
  useFormScopeOrContext,
  useIsSubmitting,
  useIsValid,
  useNativeValidity,
  useUpdateControlledField
};
//# sourceMappingURL=index.esm.js.map